/**
 * Founder fundraising-plan tool. Toggles between a questionnaire and the
 * generated plan based on whether saved answers exist (getFundraisingPlan). The
 * plan view derives outreach metrics (calcMetrics) and a staged roadmap
 * (buildPlanStages) from the saved answers; Edit answers returns to the form.
 *
 * Answers are stored as primitives on the API but use_of_funds is a comma-joined
 * string, so it's split into an array in state and re-joined on save.
 */
import React, { useEffect, useState } from 'react';
import { getFundraisingPlan, saveFundraisingPlan } from '../services/apiService';
import { USE_OF_FUNDS_OPTIONS, calcMetrics, fmt, fmtMoney, buildPlanStages, planStyles } from '../utils/fundraisingPlanUtils';
import FounderNotesWidget from '../components/FounderNotesWidget';

const STAGE_OPTIONS = [
  { value: 'idea',      label: 'Idea' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'pilot',     label: 'Pilot' },
  { value: 'revenue',   label: 'Revenue' },
];

const EMPTY = {
  raise_amount: '',
  min_check_size: '',
  use_of_funds: [],
  runway_months: '',
  dilutive: null,
  customer_count: '',
  company_stage: '',
};

/** Founder questionnaire that generates and persists a fundraising plan. */
export default function FundraisingPlan() {
  const [mode, setMode] = useState('loading'); // loading | questionnaire | plan
  const [answers, setAnswers] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getFundraisingPlan()
      .then((data) => {
        if (data) {
          setAnswers({
            raise_amount: data.raise_amount ?? '',
            min_check_size: data.min_check_size ?? '',
            use_of_funds: data.use_of_funds ? data.use_of_funds.split(',') : [],
            runway_months: data.runway_months ?? '',
            dilutive: data.dilutive,
            customer_count: data.customer_count ?? '',
            company_stage: data.company_stage ?? '',
          });
          setMode('plan');
        } else {
          setMode('questionnaire');
        }
      })
      .catch(() => setMode('questionnaire'));
  }, []);

  function toggleFund(val) {
    setAnswers((a) => ({
      ...a,
      use_of_funds: a.use_of_funds.includes(val)
        ? a.use_of_funds.filter((v) => v !== val)
        : [...a.use_of_funds, val],
    }));
  }

  function set(field) {
    return (e) => setAnswers((a) => ({ ...a, [field]: e.target.value }));
  }

  // Returns the first validation error message, or null when answers are valid.
  function validate() {
    if (!answers.raise_amount || parseFloat(answers.raise_amount) <= 0) return 'Enter the amount you want to raise.';
    if (!answers.min_check_size || parseFloat(answers.min_check_size) <= 0) return 'Enter your minimum check size.';
    if (parseFloat(answers.min_check_size) > parseFloat(answers.raise_amount)) return 'Minimum check size cannot exceed your raise amount.';
    if (!answers.runway_months) return 'Enter your runway in months.';
    if (answers.dilutive === null) return 'Answer whether you are open to dilutive capital.';
    if (!answers.company_stage) return 'Select your stage.';
    return null;
  }

  async function handleSubmit() {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSaving(true);
    try {
      await saveFundraisingPlan({
        raise_amount: parseFloat(answers.raise_amount),
        min_check_size: parseFloat(answers.min_check_size),
        use_of_funds: answers.use_of_funds.join(','),
        runway_months: parseInt(answers.runway_months, 10),
        dilutive: answers.dilutive === 'true' || answers.dilutive === true,
        customer_count: answers.customer_count ? parseInt(answers.customer_count, 10) : 0,
        company_stage: answers.company_stage,
      });
      setMode('plan');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (mode === 'loading') return <div style={styles.loading}>Loading…</div>;

  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        {mode === 'questionnaire' ? (
          <Questionnaire
            answers={answers}
            error={error}
            saving={saving}
            onSet={set}
            onToggleFund={toggleFund}
            onSubmit={handleSubmit}
          />
        ) : (
          <Plan answers={answers} onEdit={() => setMode('questionnaire')} />
        )}
      </div>
      <FounderNotesWidget />
    </div>
  );
}

// ─── Questionnaire ──────────────────────────────────────────────────────────────

function Questionnaire({ answers, error, saving, onSet, onToggleFund, onSubmit }) {
  return (
    <div>
      <div style={styles.cardHeader}>
        <h1 style={styles.cardTitle}>Fundraising Plan:</h1>
        <h1 style={{ ...styles.cardTitle, fontSize: 50, color: 'var(--color-forest)' }}>Questionnaire</h1>
      </div>

      <div style={styles.questions}>
        <Question number={1} label="How much money would you like to raise?">
          <CurrencyInput value={answers.raise_amount} onChange={onSet('raise_amount')} />
        </Question>

        <Question number={2} label="What is your minimum check size?">
          <CurrencyInput value={answers.min_check_size} onChange={onSet('min_check_size')} />
        </Question>

        <Question number={3} label="What do you plan on using the money for?" hint="Select all that apply.">
          <div style={styles.checkGroup}>
            {USE_OF_FUNDS_OPTIONS.map((opt) => (
              <label key={opt.value} style={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={answers.use_of_funds.includes(opt.value)}
                  onChange={() => onToggleFund(opt.value)}
                  style={styles.checkbox}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </Question>

        <Question number={4} label="How many months of runway do you have?">
          <div style={styles.suffixWrap}>
            <input
              type="number"
              min="0"
              style={styles.input}
              value={answers.runway_months}
              onChange={onSet('runway_months')}
              placeholder="0"
            />
            <span style={styles.suffix}>months</span>
          </div>
        </Question>

        <Question number={5} label="Are you willing to take on dilutive capital?" hint="Give up equity in your company.">
          <div style={styles.radioGroup}>
            {[{value:'true',label:'Yes'},{value:'false',label:'No'}].map((opt) => (
              <label key={opt.value} style={styles.radioLabel}>
                <input
                  type="radio"
                  name="dilutive"
                  value={opt.value}
                  checked={String(answers.dilutive) === opt.value}
                  onChange={onSet('dilutive')}
                  style={styles.radio}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </Question>

        <Question number={6} label="How many customers do you have, if any?" hint="Enter 0 if none.">
          <input
            type="number"
            min="0"
            style={{ ...styles.input, width: 140 }}
            value={answers.customer_count}
            onChange={onSet('customer_count')}
            placeholder="0"
          />
        </Question>

        <Question number={7} label="What stage are you at?">
          <div style={styles.radioGroup}>
            {STAGE_OPTIONS.map((opt) => (
              <label key={opt.value} style={styles.radioLabel}>
                <input
                  type="radio"
                  name="stage"
                  value={opt.value}
                  checked={answers.company_stage === opt.value}
                  onChange={onSet('company_stage')}
                  style={styles.radio}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </Question>
      </div>

      {error && <p style={styles.error}>{error}</p>}

      <button style={styles.submitBtn} onClick={onSubmit} disabled={saving}>
        {saving ? 'Generating…' : 'Generate my plan →'}
      </button>
    </div>
  );
}

function Question({ number, label, hint, children }) {
  return (
    <div style={styles.questionCard}>
      <p style={styles.questionLabel}>
        <span style={styles.questionNum}>{number}.</span>
        {label}
        {hint && <span style={styles.questionHint}> ({hint})</span>}
      </p>
      {children}
    </div>
  );
}

// Text input that shows a thousands-separated, 2-decimal value when blurred but
// the raw numeric string while focused; strips non-numeric input on change.
function CurrencyInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);

  const displayValue = focused
    ? value
    : value ? Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

  function handleChange(e) {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    onChange({ target: { value: raw } });
  }

  return (
    <div style={styles.currencyWrap}>
      <span style={styles.currencySign}>$</span>
      <input
        type="text"
        inputMode="numeric"
        style={styles.currencyInput}
        value={displayValue}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="0"
      />
    </div>
  );
}

// ─── Generated Plan ─────────────────────────────────────────────────────────────

// Generated plan view: summary stats, use-of-funds, and the staged roadmap
// derived from the saved answers.
function Plan({ answers, onEdit }) {
  const m = calcMetrics(answers);
  const useOfFunds = Array.isArray(answers.use_of_funds)
    ? answers.use_of_funds.map((v) => USE_OF_FUNDS_OPTIONS.find((o) => o.value === v)?.label).filter(Boolean).join(', ')
    : '';

  const stages = buildPlanStages(answers);

  return (
    <div>
      {/* Plan header */}
      <div style={planStyles.header}>
        <div>
          <h1 style={planStyles.title}>Investor Fundraising Plan</h1>
          <p style={planStyles.subtitle}>12-month raise · 3-month outreach phase</p>
        </div>
        <button style={planStyles.editBtn} onClick={onEdit}>Edit answers</button>
      </div>

      {/* Summary stats */}
      <div style={planStyles.statsRow}>
        <Stat label="Target raise"      value={fmtMoney(parseFloat(answers.raise_amount))} />
        <Stat label="Min. check size"   value={fmtMoney(parseFloat(answers.min_check_size))} />
        <Stat label="Checks needed"     value={fmt(m.numChecks)} />
        <Stat label="Total contacts"    value={fmt(m.totalContacts)} />
        <Stat label="Monthly contacts"  value={fmt(m.monthlyContacts)} />
        <Stat label="Weekly contacts"   value={fmt(m.weeklyContacts)} />
      </div>

      {useOfFunds && (
        <div style={planStyles.useRow}>
          <span style={planStyles.useLabel}>Use of funds:</span>
          <span style={planStyles.useValue}>{useOfFunds}</span>
        </div>
      )}

      {/* Stages */}
      {stages.map((s, i) => (
        <div key={i} style={planStyles.stage}>
          <h2 style={planStyles.stageTitle}>{s.title}</h2>
          <p style={planStyles.stageSubtitle}>{s.subtitle}</p>
          <ul style={planStyles.bullets}>
            {s.bullets.map((b, j) => (
              <li key={j} style={planStyles.bullet}>{b}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={planStyles.stat}>
      <p style={planStyles.statValue}>{value}</p>
      <p style={planStyles.statLabel}>{label}</p>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────────

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: 760, margin: '0 auto', paddingTop: 'var(--space-6)' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'var(--font-body)', color: 'var(--color-forest)' },

  cardHeader: { textAlign: 'center', marginBottom: 'var(--space-8)', paddingTop: 'var(--space-4)' },
  cardTitle: { fontFamily: 'var(--font-accent)', fontSize: 'var(--font-size-page-title)', fontWeight: 'var(--font-weight-page-title)', color: 'var(--color-rust)', lineHeight: 1.15 },

  questions: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  questionCard: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', padding: '28px 36px' },
  questionLabel: { fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: 'var(--color-forest)', marginBottom: 'var(--space-3)', lineHeight: 1.4 },
  questionNum: { marginRight: 4 },
  questionHint: { fontWeight: 700, opacity: 0.65 },

  currencyWrap: { display: 'flex', alignItems: 'center', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', background: '#fff', width: 200, overflow: 'hidden' },
  currencySign: { padding: '9px 10px', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, color: 'var(--color-forest)', borderRight: '1.5px solid var(--color-mint-dark)', background: 'var(--color-mint)' },
  currencyInput: { flex: 1, padding: '9px 10px', border: 'none', outline: 'none', fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', background: 'transparent' },

  input: { padding: '9px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', outline: 'none' },
  suffixWrap: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)' },
  suffix: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.6 },

  checkGroup: { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', cursor: 'pointer' },
  checkbox: { width: 16, height: 16, accentColor: 'var(--color-forest)', cursor: 'pointer', flexShrink: 0 },

  radioGroup: { display: 'flex', gap: 'var(--space-5)' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', cursor: 'pointer' },
  radio: { width: 16, height: 16, accentColor: 'var(--color-forest)', cursor: 'pointer', flexShrink: 0 },

  error: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-overdue)', marginTop: 'var(--space-5)', textAlign: 'center' },
  submitBtn: { marginTop: 'var(--space-8)', width: '100%', padding: '13px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 800, border: 'none', cursor: 'pointer' },
};

